import { fromDatabaseType, isSupportedDatabaseDocumentType } from "@asap/shared/document/document-types.utils";
import type { DatabaseDocumentType } from "@asap/shared/document/types";
import { NationalIdCardHandler } from "./document-types/national-id-card";
import { PassportHandler } from "./document-types/passport";
import { ResidencePermitHandler } from "./document-types/residence-permit";
import { CompanyRemunerationReferenceHandler } from "./document-types/company-remuneration-reference";
import { ProfessionalCardHandler } from "./document-types/professional-card";
import { MedicalCertificateHandler } from "./document-types/medical-certificate";
import { ProofOfAddressHandler } from "./document-types/proof-of-address";
import { RibHandler } from "./document-types/rib";
import { SocialSecurityCardHandler } from "./document-types/social-security-card";
import type { AnyDocumentTypeHandler } from "./handler";
import type { DocumentType } from "./types";

/**
 * Registry of all document type handlers
 */
export const HANDLERS: Record<DocumentType, AnyDocumentTypeHandler> = {
  national_id_card: NationalIdCardHandler,
  passport: PassportHandler,
  residence_permit: ResidencePermitHandler,
  company_remuneration_reference: CompanyRemunerationReferenceHandler,
  professional_card: ProfessionalCardHandler,
  medical_certificate: MedicalCertificateHandler,
  proof_of_address: ProofOfAddressHandler,
  rib: RibHandler,
  social_security_card: SocialSecurityCardHandler,
};

/**
 * Get handler by frontend document type
 * @throws Error if handler not found
 */
export function getHandler(type: DocumentType): AnyDocumentTypeHandler {
  const handler = HANDLERS[type];
  if (!handler) {
    throw new Error(`Unknown document type: ${type}`);
  }
  return handler;
}

// Legacy types with no handler of their own: verifying them stores a document of the handler's type in their place.
const LEGACY_DATABASE_TYPE_HANDLERS: Partial<Record<DatabaseDocumentType, AnyDocumentTypeHandler>> = {
  admin__residence__proof_of_address_of_the_host: ProofOfAddressHandler,
};

/**
 * Find handler by database document type
 * @returns Handler or null if not found (unsupported database type)
 */
export function findHandlerByDatabaseType(databaseType: DatabaseDocumentType): AnyDocumentTypeHandler | null {
  if (isSupportedDatabaseDocumentType(databaseType)) {
    const type = fromDatabaseType(databaseType);
    return HANDLERS[type];
  }
  return LEGACY_DATABASE_TYPE_HANDLERS[databaseType] ?? null;
}
